import { useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type AccountSettings, type Availability, type ClinicSettings, type RoomItem, type ServiceItem, type UpdateClinicProfileRequest } from '../infrastructure/settings.api';
import { prescriptionLayoutKeys } from '@/shared/prescription';
import { settingsKeys } from './settings.query';

function command<TInput, TOutput>(mutationFn: (input: TInput) => Promise<TOutput>, key: readonly string[]) {
  return function useCommand() {
    const client = useQueryClient();
    return useMutation({ mutationFn: (input: TInput) => mutationFn(input), onSuccess: () => void client.invalidateQueries({ queryKey: key }) });
  };
}

/**
 * Clinic profile and clinic settings both feed the prescription letterhead and format, so saving either must also
 * drop the cached prescription layout — otherwise the visit workspace and prescription desk keep printing the
 * previous branding until the cache expires.
 */
function clinicPrintCommand<TInput, TOutput>(mutationFn: (input: TInput) => Promise<TOutput>, key: readonly string[]) {
  return function useCommand() {
    const client = useQueryClient();
    return useMutation({
      mutationFn: (input: TInput) => mutationFn(input),
      onSuccess: (result) => {
        client.setQueryData(key, result);
        void client.invalidateQueries({ queryKey: prescriptionLayoutKeys.all });
      },
    });
  };
}

export const useUpdateClinicProfileCommand = clinicPrintCommand<UpdateClinicProfileRequest, unknown>(settingsApi.updateClinicProfile, settingsKeys.profile);
export const useUpdateClinicSettingsCommand = clinicPrintCommand<ClinicSettings, unknown>(settingsApi.updateClinicSettings, settingsKeys.clinic);
export const useAddServiceCommand = command<Omit<ServiceItem, 'id' | 'isActive'>, unknown>(settingsApi.addService, settingsKeys.services);
export const useUpdateServiceCommand = command<ServiceItem, unknown>(settingsApi.updateService, settingsKeys.services);
export const useAddRoomCommand = command<string, unknown>(settingsApi.addRoom, settingsKeys.rooms);
export const useUpdateRoomCommand = command<RoomItem, unknown>(settingsApi.updateRoom, settingsKeys.rooms);
export const useReplaceAvailabilityCommand = command<Pick<Availability, 'clinicDays' | 'providers' | 'holidays'>, unknown>(settingsApi.replaceAvailability, settingsKeys.availability);
export const useCreateLeaveCommand = command<{ startDate: string; endDate: string; startTime?: string | null; endTime?: string | null; reason: string }, unknown>(settingsApi.createLeave, settingsKeys.availability);
export const useDecideLeaveCommand = command<{ leaveId: string; decision: 'Approved' | 'Rejected' }, unknown>(settingsApi.decideLeave, settingsKeys.availability);
export const useCancelLeaveCommand = command<string, unknown>(settingsApi.cancelLeave, settingsKeys.availability);
export const useUpdateAccountSettingsCommand = command<Omit<AccountSettings, 'twoFactorEnabled'>, unknown>(settingsApi.updateAccountSettings, settingsKeys.account);
export const useRevokeSessionCommand = command<string, unknown>(settingsApi.revokeSession, settingsKeys.sessions);

export function useSetTwoFactorCommand() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ enabled, otpCode }: { enabled: boolean; otpCode?: string }) => settingsApi.setTwoFactor(enabled, otpCode),
    onSuccess: () => void client.invalidateQueries({ queryKey: settingsKeys.account }),
  });
}
